import { useState, useEffect } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Stack } from "@mui/material";
import { TRowItem } from "../../components/Table/TableRow";
import DamsTable from "../../components/Table/CollapsibleTable";

import {
  AssetAssignmentLog,
  IPagination,
  IQueryPerams,
} from "../../config/types";
import { usePagination } from "../../hooks/usePagination";
import { useMangeTableItems } from "../../hooks/useManageTableItems";

import { assignmentApi } from "../../services/assets/asset-assignment";

import { formatDate } from "../../utils/common";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { BadgeLabel } from "../../components/Label";
import { ModalBase } from "../../components/Modals/ModalBase";
import { DamsAppAsset } from "../../assets/registry";
import ModalItemInput from "../../components/Input/ModalItemInput";

const InBoundAssetAssignmentPage = () => {
  const [dataset, setDataset] = useState<TRowItem[]>([]);

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<AssetAssignmentLog[]>(assignmentApi.getInBoundAssignments);

  const [showModel, setShowModel] = useState<{
    state: boolean;
    isbatch?: boolean;
    item?: TRowItem;
  }>({ state: false });

  const [rejectionReason, setRejectionReason] = useState("");
  const [querySet] = useState<IQueryPerams>({ is_deleted: false });
  const populateDataSet = (
    data: AssetAssignmentLog[],
    pagination?: IPagination
  ) => {
    const formatted_data = data.map((record) => ({
      ...record,
      asset_brand: `${record.asset_brand}(${record.asset_model})`,
      user_email: `${record.user_email}  (${
        record.user_first_name + record.user_last_name
      })`,
      created_at: formatDate(record.created_at),
    }));
    setDataset(formatted_data);
    updatePaginationInitState(pagination);
  };
  const fetchData = async () => {
    const result = await assignmentApi.getInBoundAssignments();
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };
  useEffect(() => {
    fetchData();
    resetSelectedRowItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRejectionForm = (item?: TRowItem, callBackFtn?: () => void) => {
    setShowModel({
      state: true,
      isbatch: callBackFtn ? true : false,
      item,
    });
    if (callBackFtn) {
      callBackFtn();
    }
  };
  const closeRejectionForm = (callBackFtn?: () => void) => {
    setShowModel((p) => ({ ...p, state: false }));
    setRejectionReason("");
    if (callBackFtn) {
      callBackFtn();
    }
  };

  async function acceptRequest(asset_id: string, callBackFtn?: () => void) {
    const res = await assignmentApi.acceptAssignment(asset_id);
    if (res.success) {
      fetchData();
    }
    alertDialogStore.open({
      title: "Accept Asset",
      subtitle: res.message,
      isSuccess: res.success,
      rightBtnTxt: res.success ? "Continue" : "Okay",
    });
    if (callBackFtn) {
      callBackFtn();
    }
  }

  async function rejectRequest(asset_id: string) {
    const res = await assignmentApi.rejectAssignment(asset_id, rejectionReason);
    if (res.success) {
      fetchData();
    }
    alertDialogStore.open({
      title: "Asset Assignment Rejection",
      subtitle: res.message,
      isSuccess: res.success,
    });
  }

  const submitRejectionForm = () => {
    if (!rejectionReason) {
      alertDialogStore.open({
        title: "Action Required",
        subtitle: `Please provide reason for this rejection`,
        isSuccess: false,
        rightBtnTxt: "Okay",
      });

      return;
    }
    const record = showModel.isbatch === true ? undefined : showModel.item;
    toggleRejectButton(record);
  };

  function toggleAcceptButton(item?: TRowItem) {
    const record = item as AssetAssignmentLog;
    actionDialogStore.open({
      title: "Accept Asset Assignment",
      subtitle: `You are about to accept the assignment for "${record?.asset_brand}" (Model: ${record?.asset_model}). Click "Confirm" to proceed.`,
      onRightBtnPress: async () => acceptRequest(item?.id),
    });
  }
  function toggleRejectButton(item?: TRowItem) {
    actionDialogStore.open({
      title: item
        ? "Reject Asset Assignment"
        : "Reject Multiple Asset Assignments",
      subtitle:
        "Are you sure you want to reject this asset assignment? This action cannot be undone.",
      onRightBtnPress: async () => rejectRequest(item?.id),
    });
  }

  const renderTableActions = ({ item }: { item: TRowItem }) => {
    const record = item as AssetAssignmentLog;
    if (record.asset_assignment_status !== "pending") {
      return null;
    }
    return (
      <Stack direction={"row"} spacing={2}>
        <BadgeLabel
          onClick={() => toggleAcceptButton(item)}
          title={"Accept"}
          colorType="green"
          tip="Click to accept asset"
        />
        <BadgeLabel
          onClick={() => openRejectionForm(item)}
          title={"Reject"}
          colorType="red"
          tip="Click to reject asset"
        />
      </Stack>
    );
  };
  return (
    <MainPageBase
      pageState={"main"}
      title={"Pending Asset Assignments"}
      subtitle={
        "Assets assigned to you that are awaiting your acceptance or rejection."
      }
      exportBtnOnClick={() => {
        alert("Exporting...");
      }}
      exportBtnText={"Export"}
    >
      <ModalBase
        open={showModel.state}
        title={"Reject Distribution Asset"}
        subtitle={""}
        type={"add-item"}
        showHeader={true}
        leftIcon={DamsAppAsset.icons.warning}
        leftBtnTxt="Cancel"
        onLeftBtnPress={closeRejectionForm}
        rightBtnTxt={"Reject"}
        onRightBtnPress={() => submitRejectionForm()}
        onClose={closeRejectionForm}
      >
        <Stack gap={2}>
          <ModalItemInput
            label="Reason"
            placeholder="Enter reason"
            handleChange={(e) => setRejectionReason(e.target.value)}
            value={rejectionReason}
            errorMessage={"Please provide reason for rejection "}
            multiline
            rows={5}
          />
        </Stack>
      </ModalBase>

      <Box height={`100%`} display={"flex"} justifyContent={"center"}>
        <Stack width={"100%"}>
          <DamsTable
            tableTitle="Asset Assignment Logs"
            tableSubtitle="Inbound asset assignments that are pending your confirmation — review and take action to accept or reject."
            numTableRecords="25"
            tableHeaders={columns}
            tableKeys={tkeys}
            data={dataset}
            maxRowCells={6}
            hiddenKeys={hkeys}
            hiddenLabels={hcolumns}
            useTableCheckbox
            onTableItemSelect={toggleRow}
            toggleSelectAllRow={toggleSelectAllRow}
            selectedRowItems={selectedRowItems}
            hasPagination
            paginationState={pagination}
            onNextPage={paginateToNextPage(querySet, populateDataSet)}
            onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
            setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            extraRowComponent={renderTableActions}
          />
        </Stack>
      </Box>
    </MainPageBase>
  );
};

const tkeys: (keyof AssetAssignmentLog)[] = [
  "asset_brand",
  "asset_serial_number",
  "user_email",
  "user_category",
  "asset_assignment_status",
  "created_at",
];

const columns = [
  "Asset Brand (Model)",
  "Serial Number",
  "Assigned User - Email (Full Name)",
  "User Category",
  "Assignment Status",
  "Date of Assignment",
];
const hkeys: (keyof AssetAssignmentLog)[] = [
  "remarks",
  "asset_current_location",
  "user_domain",
];

const hcolumns = ["Remarks", "Asset Current Location", "User Domain"];
export default InBoundAssetAssignmentPage;